import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import './OfferTechAnalysis.scss';
import { useEffect, useMemo, useState } from 'react';
import Button from '@srm/ui/button';
import clsx from 'clsx';
import Modal from '@srm/ui/modal';
import { IOffer } from '@models/winnerSelection/ITableDemand';
import { Controller, useForm } from 'react-hook-form';
import { IOption } from '@srm/ui/types/IOption';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { sendNotification } from '@srm/ui/lib/notifications';
import ToolTipTitle from '@atoms/ToolTipTitle/ToolTipTitle';
import Hint from '@srm/ui/hint';
import { IOfferTechAnalysisFormData } from './IOfferTechAnalysisFormData';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import SelectControl from '@srm/ui/controls/SelectControl';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';

export interface IOfferTechAnalysisProps {
    tableKey: string;
    isEdit: boolean;
    value: IOffer;
    demandId: string;
    onSubmit?: (value: IOfferTechAnalysisFormData) => void;
    onClose?: () => void;
}

const OfferTechAnalysis = ({ tableKey, isEdit, value, demandId, onSubmit, onClose }: IOfferTechAnalysisProps) => {
    const [error, setError] = useState<string>();
    const methods = useForm<IOfferTechAnalysisFormData>({
        mode: 'onBlur',
    });

    const { isSubmitting, isDirty } = methods.formState;
    // Escape и клик по фону закрывают окно: набранный комментарий без вопроса не теряем
    const hasUnsavedInput = useUnsavedFormInput(methods, { textFields: ['acceptanceComment'] });

    const [acceptanceResult, setAcceptanceResult] = useState<number>();

    useEffect(() => {
        const defaultValue: IOfferTechAnalysisFormData = {
            acceptanceResult: value.acceptanceResult,
            acceptanceComment: value.acceptanceComment,
        };
        setAcceptanceResult(value.acceptanceResult);
        methods.reset(defaultValue);
    }, [value]);

    useEffect(() => {
        methods.trigger('acceptanceComment');
    }, [acceptanceResult]);

    const onFormSubmit = async (form: IOfferTechAnalysisFormData) => {
        setError(undefined);
        return WinnerSelectionService.saveTableDemandOfferTechAnalysis(value.key, demandId, tableKey, form)
            .then(() => {
                sendNotification({
                    message: 'Данные успешно сохранены',
                    variant: 'green',
                });
                onSubmit && onSubmit(form);
            })
            .catch((reason) => {
                setError(reason);
                console.log(reason);
            });
    };

    const results = useMemo(() => {
        return [
            { label: 'Соответствует', value: 1 },
            { label: 'Не соответствует', value: 0 },
        ] as IOption[];
    }, []);

    const errorLabel = error && (
        <div className="offer-ta__error">
            <Hint role="alert" title={error} tone="danger" />
        </div>
    );

    const modalFooter = (
        <div className={'modal-dlg-buttons'}>
            <Button
                type="submit"
                form="offer-ta-form"
                loading={isSubmitting}
                className={clsx('offer-ta__button', 'offer-ta__save-button')}
                disabled={isSubmitting || !isDirty}
            >
                Сохранить
            </Button>
        </div>
    );

    return (
        <Modal
            size="xl"
            onClose={() => onClose?.()}
            hasUnsavedInput={() => isEdit && hasUnsavedInput()}
            closeDisabled={isSubmitting}
            header={`Технический анализ предложения №${value.number || ''} (${value.name})`}
            footer={isEdit ? modalFooter : null}
        >
            {errorLabel}
            <FormProviderControl
                formMethods={methods}
                id="offer-ta-form"
                className="offer-ta__form"
                onSubmit={onFormSubmit}
            >
                <HookFormProvider controller={Controller}>
                    <div className="offer-ta__content">
                        <div className="offer-ta__row">
                            <SelectControl
                                label="Результат допуска"
                                name="acceptanceResult"
                                readOnly={true}
                                disabled={!isEdit}
                                highlightMenuItems={false}
                                options={results}
                                control={methods.control}
                                required={true}
                                rules={{ required: REQUIRED_MESSAGES.select }}
                                setValue={(options) => {
                                    return options[0].value;
                                }}
                                getValue={(options: IOption[], value: any) => {
                                    return options.filter((x) => x.value === value);
                                }}
                                onValueChange={(value) => {
                                    setAcceptanceResult(value[0].value);
                                }}
                            />
                        </div>
                        <div className="offer-ta__row">
                            <TextareaControl
                                label="Комментарий по соответствию"
                                name="acceptanceComment"
                                tooltip={
                                    <ToolTipTitle title="В случаях 'Не соответсвует' необходимо указать конкретные пункты ТЗ, комментарий будет внесен в обоснования критерия по соответствию ТЗ при наличии." />
                                }
                                readOnly={!isEdit}
                                disabled={!isEdit}
                                autoResize={true}
                                required={acceptanceResult === 0}
                                rules={{ required: acceptanceResult === 0 ? REQUIRED_MESSAGES.text : false }}
                                control={methods.control}
                            />
                        </div>
                    </div>
                </HookFormProvider>
            </FormProviderControl>
        </Modal>
    );
};

export default OfferTechAnalysis;
